import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  User,
  Mail,
  Briefcase,
  Image,
  Link,
  Lock,
  Info,
  GitBranch,
  FileText,
  Eye,
  Edit
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';

interface RolePermissions {
  view: boolean;
  edit: boolean;
}

interface ProfileSection {
  label: string;
  description: string;
  icon: string;
  roles: {
    employee: RolePermissions;
    manager: RolePermissions;
    hr: RolePermissions;
    admin: RolePermissions;
  };
}

interface Permissions {
  basicInfo: ProfileSection;
  contactInfo: ProfileSection;
  professionalInfo: ProfileSection;
  photo: ProfileSection;
  socialLinks: ProfileSection;
  privacySettings: ProfileSection;
}

interface Role {
  id: string;
  label: string;
  color: string;
}

const ROLE_COLOR_MAP: Record<string, string> = {
  blue: 'bg-primary/10 text-primary',
  green: 'bg-success/10 text-success',
  purple: 'bg-accent/10 text-accent-foreground',
  red: 'bg-destructive/10 text-destructive',
};

/**
 * CO-858. This page is a PREVIEW of a planned profile-field permission matrix. Nothing on it is stored
 * or enforced: there is no endpoint behind it. It used to carry a Save that waited 1.5 s and announced
 * "saved successfully", which was a lie, so the save, the reset and the editable state are gone and
 * every control is rendered disabled. The matrix below is the fixed sample the preview shows.
 *
 * Do not add an action to this page until a real feature exists behind it. The page is a demo surface
 * (`demo` in navigationModules.ts, DemoSurfaceDefaults.cs, demo-surface-switchoff.sql), hidden unless
 * an Operator switches it on for a tenant.
 */
const SAMPLE_PERMISSIONS: Permissions = {
    basicInfo: {
      label: 'Basic Information',
      description: 'Name, job title, department, location',
      icon: 'User',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: true },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true }
      }
    },
    contactInfo: {
      label: 'Contact Information',
      description: 'Email, phone number',
      icon: 'Mail',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true }
      }
    },
    professionalInfo: {
      label: 'Professional Information',
      description: 'Bio, skills, achievements',
      icon: 'Briefcase',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: false },
        admin: { view: true, edit: true }
      }
    },
    photo: {
      label: 'Profile Photo',
      description: 'Profile picture',
      icon: 'Image',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true }
      }
    },
    socialLinks: {
      label: 'Social Links',
      description: 'LinkedIn, GitHub, etc.',
      icon: 'Link',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: false },
        admin: { view: true, edit: true }
      }
    },
    privacySettings: {
      label: 'Privacy Settings',
      description: 'Public visibility controls',
      icon: 'Lock',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true }
      }
    }
};

const ROLES: Role[] = [
  { id: 'employee', label: 'Employee', color: 'blue' },
  { id: 'manager', label: 'Manager', color: 'green' },
  { id: 'hr', label: 'HR', color: 'purple' },
  { id: 'admin', label: 'Admin', color: 'red' }
];

/** Read-only sample switches of the two lower cards: label key, description key, sample state. */
const SAMPLE_WORKFLOW_OPTIONS = [
  { label: 'Require manager approval', description: 'Changes need manager approval before publishing', checked: false },
  { label: 'Require HR approval', description: 'Changes need HR approval before publishing', checked: true },
];
const SAMPLE_AUDIT_OPTIONS = [
  { label: 'Log all changes', description: 'Track all profile modifications', checked: true },
  { label: 'Notify on changes', description: 'Send notifications when profile is updated', checked: false },
];

const ProfileSettings = () => {
  const { t } = useTranslation();

  const getRoleBadgeClasses = (color: string) => {
    return ROLE_COLOR_MAP[color] || 'bg-muted text-muted-foreground';
  };

  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: React.ComponentType<{ className?: string }> } = {
      User,
      Mail,
      Briefcase,
      Image,
      Link,
      Lock
    };
    return iconMap[iconName] || User;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-8 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <Card variant="elevated" className="mb-6">
            <CardContent>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">{t('Profile Authorization Settings')}</h1>
                    <p className="text-muted-foreground mt-1">{t('Control who can view and edit public profile information')}</p>
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>

          {/* CO-858: the one honest statement on this page. Full border and tint, no side stripe. */}
          <div
            role="note"
            data-testid="profile-settings-preview-notice"
            className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6"
          >
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-warning mt-0.5 shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-foreground mb-1">{t('profileSettings.previewNotice.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('profileSettings.previewNotice.body')}</p>
              </div>
            </div>
          </div>

          {/* Permissions Table */}
          <Card variant="elevated" className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th className="px-6 py-4 text-start text-sm font-semibold text-foreground w-1/4">
                      {t('Profile Section')}
                    </th>
                    {ROLES.map(role => (
                      <th key={role.id} className="px-6 py-4 text-center text-sm font-semibold text-foreground">
                        <div className="flex items-center justify-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeClasses(role.color)}`}>
                            {t(role.label)}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {Object.entries(SAMPLE_PERMISSIONS).map(([key, section]) => {
                    const IconComponent = getIconComponent(section.icon);
                    return (
                      <tr key={key}>
                        <td className="px-6 py-4">
                          <div className="flex items-start">
                            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center me-3 flex-shrink-0">
                              <IconComponent className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{t(section.label)}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{t(section.description)}</p>
                            </div>
                          </div>
                        </td>
                        {ROLES.map(role => (
                          <td key={role.id} className="px-6 py-4">
                            <div className="flex items-center justify-center space-x-4 rtl:space-x-reverse">
                              {/* View Permission (sample, read-only) */}
                              <label className="flex items-center cursor-not-allowed">
                                <input
                                  type="checkbox"
                                  checked={section.roles[role.id as keyof typeof section.roles]?.view}
                                  disabled
                                  readOnly
                                  className="h-4 w-4 rounded border-border text-primary cursor-not-allowed"
                                />
                                <span className="ms-1.5 text-xs text-muted-foreground">{t('View')}</span>
                              </label>

                              {/* Edit Permission (sample, read-only) */}
                              <label className="flex items-center cursor-not-allowed">
                                <input
                                  type="checkbox"
                                  checked={section.roles[role.id as keyof typeof section.roles]?.edit}
                                  disabled
                                  readOnly
                                  className="h-4 w-4 rounded border-border text-success cursor-not-allowed"
                                />
                                <span className="ms-1.5 text-xs text-muted-foreground">{t('Edit')}</span>
                              </label>
                            </div>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Additional Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">

            {/* Approval Workflow */}
            <Card variant="elevated">
              <CardContent>
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <GitBranch className="w-5 h-5 me-2" />
                  {t('Approval Workflow')}
                </h2>

                <div className="space-y-3">
                  {SAMPLE_WORKFLOW_OPTIONS.map(option => (
                    <label key={option.label} className="flex items-center justify-between gap-3 p-3 bg-muted rounded-lg cursor-not-allowed">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{t(option.label)}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{t(option.description)}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={option.checked}
                        disabled
                        readOnly
                        className="h-4 w-4 shrink-0 rounded border-border text-primary cursor-not-allowed"
                      />
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Audit & Logging */}
            <Card variant="elevated">
              <CardContent>
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <FileText className="w-5 h-5 me-2" />
                  {t('Audit & Logging')}
                </h2>

                <div className="space-y-3">
                  {SAMPLE_AUDIT_OPTIONS.map(option => (
                    <label key={option.label} className="flex items-center justify-between gap-3 p-3 bg-muted rounded-lg cursor-not-allowed">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{t(option.label)}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{t(option.description)}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={option.checked}
                        disabled
                        readOnly
                        className="h-4 w-4 shrink-0 rounded border-border text-primary cursor-not-allowed"
                      />
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Legend */}
          <div className="bg-muted rounded-lg p-4 mt-6">
            <h3 className="text-sm font-semibold text-foreground mb-3">{t('Permission Legend')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-start">
                <Eye className="w-4 h-4 text-primary me-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t('View Permission')}</p>
                  <p className="text-xs text-muted-foreground">{t('Allows viewing the profile section information')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Edit className="w-4 h-4 text-success me-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t('Edit Permission')}</p>
                  <p className="text-xs text-muted-foreground">{t('Allows modifying the profile section information')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
